"use client"

import { Text } from "@medusajs/ui"

import { StoreProduct, StoreRegion } from "@/types/store"

import { normalizeTextEncoding } from "@/lib/util/text-encoding"
import {
  canQuickAddVariant,
  getProductVariantParam,
} from "@/lib/util/product-variant-url"
import { addToCart } from "@lib/data/cart"
import PlaceholderImage from "@modules/common/icons/placeholder-image"
import Rating from "@mui/material/Rating"
import { Heart, Info, ShoppingCart } from "lucide-react"
import Image from "next/image"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { MouseEvent, useState, useMemo } from "react"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import VariantSelectionModal from "../variant-selection-modal"
import CalculateModal from "../calculate-modal"
import { normalizeUnitType } from "@lib/util/normalize-unit"
import { resolveImageUrl } from "@lib/util/image-url"

export default function ProductPreview({
  product,
  isFeatured,
  region,
  variant,
  averageRating,
}: {
  product: StoreProduct
  isFeatured?: boolean
  region: StoreRegion
  variant?: boolean
  averageRating?: number
}) {
  const { t } = useTranslation()
  const router = useRouter()
  const [isAdding, setIsAdding] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [showCalculateModal, setShowCalculateModal] = useState(false)
  const [modalMode, setModalMode] = useState<"favorite" | "addToCart">(
    "favorite"
  )

  const isProductFavorited = useMemo(() => {
    return product.variants?.some((v) => v.is_favorite) || false
  }, [product.variants])

  const handleFavoriteClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setModalMode("favorite")
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  const variantCount = product.variants?.length ?? 0
  const hasMultipleVariants = variantCount > 1
  let thumbnailUrl = null
  const primaryVariant = product?.variants?.[0]
  const categoryName = product.categories?.[0]?.name
  const marketPrice = product?.variants?.[0]?.market_price ?? 0
  const salesPrice = product?.variants?.[0]?.sales_price ?? 0
  const hasPrice = Boolean(salesPrice)
  const hasDiscount = hasPrice && marketPrice > salesPrice
  const discountPercent = hasDiscount
    ? Math.round(((marketPrice - salesPrice) / marketPrice) * 100)
    : 0
  const isCalculationHelperEnabled = product.include_in_calculation_helper === 1

  // default to product images
  if (product.images && product.images.length) {
    const thumbnail = product.images?.find((image) => image.is_thumbnail === 1)
    if (thumbnail) {
      thumbnailUrl = resolveImageUrl(thumbnail.image_key)
    }
  }

  // if it has variants and they have images, and there is no product image
  // use these as a fallback
  if (thumbnailUrl === null && Array.isArray(product?.variants)) {
    // collect all images from all variants
    const allImages = product.variants.flatMap((v) => v.images || [])
    // find the first thumbnail
    const thumbnail = allImages.find((img) => img.is_thumbnail === 1)

    if (thumbnail) {
      thumbnailUrl = resolveImageUrl(thumbnail.image_key)
    }
  }

  const targetVariantId = primaryVariant?.id
  const productUrl = `/products/${product.handle}${
    variant
      ? `?variant=${getProductVariantParam(product?.variants?.[0]?.title)}`
      : ""
  }`
  const isInfiniteStock = primaryVariant?.stock_type === "infinite"
  const isInStock =
    isInfiniteStock ||
    Boolean(primaryVariant?.allow_backorder) ||
    (primaryVariant?.inventory_quantity ?? 0) > 0
  const isOutOfStock = !isInStock || !hasPrice
  const canQuickAdd = canQuickAddVariant(primaryVariant)

  const handleAddToCart = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()

    if (hasMultipleVariants) {
      setModalMode("addToCart")
      setIsModalOpen(true)
      return
    }

    if (!canQuickAdd || !targetVariantId) {
      router.push(productUrl)
      return
    }

    setIsAdding(true)
    try {
      await addToCart({
        variantId: String(targetVariantId),
        quantity: 1,
        formAnswers: {},
      })
      toast.success(t("product.addToCart"))
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("cart:open"))
      }
    } catch (error: any) {
      toast.error(error?.message || t("product.addToCartFailed"))
    } finally {
      setIsAdding(false)
    }
  }

  return (
    <div className="group flex flex-col justify-between h-full cursor-pointer overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-orange-200 hover:shadow-xl">
      <VariantSelectionModal
        product={product}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        mode={modalMode}
      />
      {primaryVariant && (
        <CalculateModal
          isOpen={showCalculateModal}
          closeModal={() => setShowCalculateModal(false)}
          variant={primaryVariant}
          unitPriceType={product.unit_price_type_name}
        />
      )}
      <Link href={productUrl}>
        <div className="relative aspect-[5.3/7.5] h-72 w-full overflow-hidden bg-slate-50">
          {thumbnailUrl ? (
            <Image
              src={thumbnailUrl}
              alt={""}
              layout="fill"
              objectFit="cover"
              className="transition-transform duration-500 ease-out group-hover:scale-105"
            />
          ) : (
            <PlaceholderImage className="w-full h-full" />
          )}

          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/5 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

          {isOutOfStock && (
            <div className="pointer-events-none absolute inset-0 z-20 overflow-hidden">
              <div className="absolute left-[-35%] top-[52%] w-[170%] -rotate-[24deg] bg-black/55 py-2 text-center">
                <span className="text-base font-extrabold tracking-wide text-white">
                  TÜKENMİŞTİR
                </span>
              </div>
            </div>
          )}
        </div>
        <div className="mt-0 flex flex-col gap-1.5 p-3 sm:p-4">
          <div className="flex justify-between txt-compact-medium">
            <Text
              className="line-clamp-2 min-h-[40px] sm:min-h-[48px] text-base sm:text-xl font-bold leading-tight sm:leading-6 text-slate-700"
              data-testid="product-title"
            >
              {normalizeTextEncoding(product.title ?? "")}
            </Text>
          </div>
          <div className="flex min-h-[20px] flex-wrap items-center gap-1.5">
            {categoryName && (
              <span className="rounded-full border border-orange-200 bg-orange-50 px-2 py-0.5 text-[10px] sm:text-[11px] font-bold uppercase tracking-wide text-orange-700">
                {normalizeTextEncoding(categoryName)}
              </span>
            )}
            {hasDiscount && discountPercent > 0 && (
              <span className="rounded-full bg-orange-500 px-2 py-0.5 text-[10px] sm:text-[11px] font-bold text-white">
                %{discountPercent}
              </span>
            )}
          </div>
          <div className="flex min-h-[20px] items-center justify-between gap-1">
            {typeof averageRating === "number" && averageRating > 0 && (
              <Rating
                name="simple-controlled"
                value={averageRating}
                precision={0.5}
                size="small"
                readOnly
              />
            )}
          </div>
          <div className="flex items-end justify-between pt-1 txt-compact-medium">
            {hasPrice ? (
              <div className="flex flex-row items-center justify-between">
              <div className="flex flex-col">
                <Text
                  className="text-sm sm:text-lg font-medium text-slate-500 line-through"
                  data-testid="product-title"
                >
                  {marketPrice.toLocaleString("tr-TR", {
                    style: "currency",
                    currency: "TRY",
                  })}
                </Text>
                <Text
                  className="text-2xl sm:text-4xl font-black leading-none text-slate-900"
                  data-testid="product-title"
                >
                  {salesPrice.toLocaleString("tr-TR", {
                    style: "currency",
                    currency: "TRY",
                  })}
                </Text>
              </div>
              {product.unit_price_type_name ? (
                <Text className="ml-1 sm:ml-2 text-xs sm:text-sm font-semibold text-slate-500">
                    {t(`cartDropdown.unitTypes.${normalizeUnitType(product.unit_price_type_name)}` as any)}
                </Text>
              ): (
                <Text className="ml-1 sm:ml-2 text-xs sm:text-sm font-semibold text-slate-500">
                      {t("product.quantity")}
                </Text>
              )}
              </div>
            ) : (
              <Text className="text-xs sm:text-sm font-semibold text-slate-500">
                {t("product.productBeingSupplied")}
              </Text>
            )}
          </div>
        </div>
      </Link>
      <div className="flex w-full items-center gap-1.5 px-3 pb-3 sm:px-4 sm:pb-4">
        {!isOutOfStock && (
          <div className={`group/quick-add relative flex flex-1 ${isCalculationHelperEnabled ? 'gap-1.5' : ''}`}>
            {isCalculationHelperEnabled ? (
              <>
                <button
                  type="button"
                  onClick={handleAddToCart}
                  disabled={isAdding}
                  className="flex h-9 sm:h-10 flex-1 items-center justify-center rounded-xl border border-orange-200 bg-orange-50 text-[11px] sm:text-sm font-semibold text-orange-700 transition-colors hover:bg-orange-100 disabled:opacity-60"
                >
                  <ShoppingCart className="mr-1 sm:mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  {isAdding ? t("product.addingToCart") : t("product.addToCart")}
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowCalculateModal(true);
                  }}
                  className="flex h-9 sm:h-10 flex-1 items-center justify-center rounded-xl border border-orange-600 bg-orange-600 text-[11px] sm:text-sm font-semibold text-white transition-colors hover:bg-orange-700"
                >
                  {t("product.calculate")}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={handleAddToCart}
                disabled={isAdding}
                className="flex h-9 sm:h-10 w-full items-center justify-center rounded-xl border border-orange-200 bg-orange-50 text-[11px] sm:text-sm font-semibold text-orange-700 transition-colors hover:bg-orange-100 disabled:opacity-60"
              >
                <ShoppingCart className="mr-1 sm:mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4" />
                {isAdding
                  ? t("product.addingToCart")
                  : hasMultipleVariants || canQuickAdd
                    ? t("product.addToCart")
                    : t("product.viewDetails")}
                {!hasMultipleVariants && !canQuickAdd && (
                  <Info className="ml-1 sm:ml-2 h-3 w-3 sm:h-3.5 sm:w-3.5" />
                )}
              </button>
            )}
            {!isCalculationHelperEnabled && !hasMultipleVariants && !canQuickAdd && (
              <div className="pointer-events-none absolute -top-12 left-1/2 z-20 w-max -translate-x-1/2 rounded-lg border border-orange-200 bg-white px-2 py-1 text-[10px] sm:text-xs font-medium text-slate-700 opacity-0 shadow-md transition-opacity duration-200 group-hover/quick-add:opacity-100">
                {t("product.variantRequiredGoToDetails")}
              </div>
            )}
          </div>
        )}
        <button
          type="button"
          aria-label={
            isProductFavorited
              ? t("product.removeFromFavorites")
              : t("product.addToFavorites")
          }
          onClick={handleFavoriteClick}
          className={`inline-flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-xl border border-orange-200 bg-white text-orange-600 transition-colors hover:bg-orange-50 ${isOutOfStock ? "flex-1" : ""}`}
        >
          <Heart
            className="h-3.5 w-3.5 sm:h-4 sm:w-4"
            fill={isProductFavorited ? "currentColor" : "none"}
          />
        </button>
      </div>
    </div>
  )
}
